import { discover } from "../graph/discover.ts";
import type { DslParser } from "../graph/dsl/index.ts";
import { check } from "../graph/dsl/index.ts";
import type { DirectedGraph } from "../graph/types.ts";
import { isNode } from "../graph/types.ts";
import type { AssessmentScope } from "../types/assessment.ts";
import type {
  AssessmentNodes,
  NodeFor,
  TargetNodeType,
} from "../types/assessment/data/index.ts";
import { assessmentParse } from "./dsl.ts";
import { names as gcpServices } from "./media/GoogleCloud.ts";
import { awsServices } from "./media/aws-services.ts";

export type InventoryItem<N extends TargetNodeType> = {
  label: string;
  filter: (data: NodeFor<N>["data"]) => boolean;
  predicate?: (scopes: AssessmentScope[]) => boolean;
  show: N;
  where: string;
  tooltip?: string;
};

const InventoryItem = <N extends TargetNodeType>(link: InventoryItem<N>) =>
  link;

export const generateInventoryData = (targets: AssessmentScope[]) => {
  const isAws = targets.some((t) => t.integration === "aws");
  const isGcp = targets.some((t) => t.integration === "gcloud");
  const isAzureAd = targets.some((t) => t.integration === "azure-ad");
  const isWorkspace = targets.some((t) => t.integration === "workspace");
  const isCustom = targets.some((t) => t.integration === "custom");

  // Since each item has the same "show" as query keytype, and every query term is a "first"
  // match, can achieve a "show only" behavior using a simple `{type}=...` query.
  //
  // If the "show" was different than the keytype, we would instead have to use the
  // `^{type}={attribute}:!"{keyword}"` pattern.
  return {
    credential: {
      _heading: InventoryItem({
        filter: () => true,
        label: "Credentials",
        show: "credential",
        where: "",
      }),
      key: InventoryItem({
        filter: ({ type }) => type === "key",
        label: "Keys",
        show: "credential",
        where: 'credential=type:"key"',
      }),
      federatedLogin: InventoryItem({
        filter: ({ type }) => type === "federated",
        label: "Federated Login",
        show: "credential",
        where: 'credential=type:"federated"',
      }),
      shortLived: InventoryItem({
        filter: ({ type }) => type === "short-lived",
        label: "Short-lived Login",
        show: "credential",
        where: 'credential=type:"short-lived"',
      }),
      password: InventoryItem({
        filter: ({ type }) => type === "password",
        label: "Passwords",
        show: "credential",
        where: 'credential={type:"password"}',
        tooltip: "Passwords for authentication.",
      }),
      ...(isAws
        ? {
            password: InventoryItem({
              filter: ({ type }) => type === "password",
              label: "Passwords",
              show: "credential",
              where: 'credential=type:"password"',
            }),
          }
        : {}),
    },
    identity: {
      _heading: InventoryItem({
        filter: () => true,
        label: "Identities",
        show: "identity",
        where: "",
      }),
      user: InventoryItem({
        filter: ({ type }) => type === "user",
        label: "Users",
        show: "identity",
        where: 'identity=type:"user"',
      }),
      group: InventoryItem({
        filter: ({ type }) => type === "group",
        label: "Groups",
        show: "identity",
        where: 'identity=type:"group"',
      }),
      federated: InventoryItem({
        filter: ({ type }) => type === "federated",
        label: "Federated Identities",
        show: "identity",
        where: 'identity=type:"federated"',
        tooltip: "Federated identities for external authentication.",
      }),
      ...(isAws
        ? {
            awsIamRoles: InventoryItem({
              filter: ({ provider, type }) =>
                provider === "aws" && type === "aws-iam-role",
              label: "AWS IAM Roles",
              predicate: (scopes) =>
                scopes.some((s) => s.integration === "aws"),
              show: "identity",
              where: 'identity={type:"aws-iam-role" provider:"aws"}',
            }),
            federatedRoles: InventoryItem({
              filter: ({ provider, type }) =>
                provider === "aws" && type === "federated",
              label: "AWS Federated Access Roles",
              predicate: (scopes) =>
                scopes.some((s) => s.integration === "aws"),
              show: "identity",
              where: 'identity={type:"federated" provider:"aws"}',
              tooltip:
                "Roles whose trust relationship allow access via OIDC or SAML.",
            }),
            permissionSetRoles: InventoryItem({
              filter: ({ provider, type }) =>
                provider === "aws" && type === "aws-permission-set-role",
              label: "AWS Permission-Set Roles",
              predicate: (scopes) =>
                scopes.some((s) => s.integration === "aws"),
              show: "identity",
              where: 'identity={type:"aws-permission-set-role" provider:"aws"}',
              tooltip: "Roles generated by assigning AWS permission sets.",
            }),
          }
        : {}),
      ...(isGcp
        ? {
            gcpServiceAccounts: InventoryItem({
              filter: ({ provider, type }) =>
                provider === "gcp" && type === "service-account",
              label: "GCP Service Accounts",
              predicate: (scopes) =>
                scopes.some((s) => s.integration === "gcloud"),
              show: "identity",
              where: 'identity={type:"service-account" provider:"gcp"}',
            }),
          }
        : {}),
      ...(isAzureAd
        ? {
            azureAdAppRegistration: InventoryItem({
              filter: ({ provider, type }) =>
                provider === "azure-ad" && type === "entra-app-registration",
              label: "Entra ID App Registrations",
              predicate: (scopes) =>
                scopes.some((s) => s.integration === "azure-ad"),
              show: "identity",
              where:
                'identity={type:"entra-app-registration" provider:"azure-ad"}',
            }),
            azureAdServicePrincipal: InventoryItem({
              filter: ({ provider, type }) =>
                provider === "azure-ad" && type === "service-account",
              label: "Entra ID Service Principals",
              predicate: (scopes) =>
                scopes.some((s) => s.integration === "azure-ad"),
              show: "identity",
              where: 'identity={type:"service-account" provider:"azure-ad"}',
            }),
          }
        : {}),
      kubernetes: InventoryItem({
        filter: ({ provider }) => provider === "k8s",
        label: "Kubernetes Service Accounts",
        show: "identity",
        where: 'identity={type:"service-account" provider:"k8s"}',
      }),
    },
    grant: {
      _heading: InventoryItem({
        filter: () => true,
        label: "Entitlements",
        show: "grant",
        where: "",
      }),
      ...(isWorkspace
        ? {
            adminRoleBinding: InventoryItem({
              filter: ({ provider }) => provider === "workspace",
              label: "Workspace Admin Role Bindings",
              predicate: (scopes) =>
                scopes.some((s) => s.integration === "workspace"),
              show: "grant",
              where: 'entitlement=provider:"workspace"',
            }),
          }
        : {}),
      ...(isAws
        ? {
            awsPolicyAssignemnt: InventoryItem({
              filter: ({ provider }) => provider === "aws",
              label: "AWS Policy Assignments",
              predicate: (scopes) =>
                scopes.some((s) => s.integration === "aws"),
              show: "grant",
              where: 'entitlement=provider:"aws"',
            }),
          }
        : {}),
      ...(isGcp
        ? {
            gcpRoleBinding: InventoryItem({
              filter: ({ provider }) => provider === "gcp",
              label: "GCP Role Bindings",
              predicate: (scopes) =>
                scopes.some((s) => s.integration === "gcloud"),
              show: "grant",
              where: 'entitlement=provider:"gcp"',
            }),
          }
        : {}),
      ...(isCustom
        ? {
            customEntitlements: InventoryItem({
              filter: ({ provider, isProviderManaged }) =>
                provider === "custom" && isProviderManaged === false,
              label: "Custom Entitlements",
              show: "grant",
              where: 'entitlement={provider:"custom" isProviderManaged:false}',
              tooltip: "User-defined policies with custom permission rules.",
            }),
          }
        : {}),
      ...(isAzureAd
        ? {
            azureAdRoleAssignment: InventoryItem({
              filter: ({ provider }) => provider === "azure-ad",
              label: "Entra ID Role Assignments",
              predicate: (scopes) =>
                scopes.some((s) => s.integration === "azure-ad"),
              show: "grant",
              where: 'entitlement=provider:"azure-ad"',
            }),
          }
        : {}),
    },
    resource: {
      _heading: InventoryItem({
        filter: () => true,
        label: "Resources",
        show: "resource",
        where: "",
        tooltip: "Resources are only available at a single scope level.",
      }),
      ...(isAws
        ? Object.entries(awsServices).reduce(
            (acc, [service, name]) => {
              acc[`aws-${service}`] = InventoryItem({
                filter: (data) => data.service === service,
                label: name,
                show: "resource",
                where: `resource=service:"${service}"`,
                predicate: (scopes) =>
                  scopes.some((s) => s.integration === "aws"),
              });
              return acc;
            },
            {} as Record<string, InventoryItem<"resource">>
          )
        : {}),
      ...(isGcp
        ? Object.entries(gcpServices).reduce(
            (acc, [service, label]) => {
              acc[`gcp-${service}`] = InventoryItem({
                filter: (data) => data.service === service,
                label: label,
                show: "resource",
                where: `resource=service:"${service}"`,
                predicate: (scopes) =>
                  scopes.some((s) => s.integration === "gcloud"),
              });
              return acc;
            },
            {} as Record<string, InventoryItem<"resource">>
          )
        : {}),
    },
  };
};

const _validateInventoryData: (
  scopes: AssessmentScope[]
) => Record<string, Record<string, InventoryItem<any>>> = generateInventoryData;

export const countNodes = async (args: {
  link: InventoryItem<any>;
  graph: DirectedGraph<AssessmentNodes>;
  parser: DslParser;
}) =>
  await discover(
    args.graph,
    isNode(args.link.show),
    check(assessmentParse(args.link.where)(args.parser))
  );
